import React, { useState, useEffect } from "react";
import {
  History,
  Calendar,
  FileText,
  Eye,
  CheckCircle,
  Clock,
  Package,
  LayoutGrid,
  List,
  RefreshCw,
} from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import { API_BASE_URL } from "../../utils/apiConfig";

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  countries?: string[];
}

interface PickupList {
  _id: string;
  pickupListNo: string;
  pickupDate: string;
  issuedAt: string;
  printedAt?: string;
  status: string;
  shippingCompany: string;
  pdfGenerated?: boolean;
}

interface PickupHistoryProps {
  currentUser: User;
  onOpenPdfPreview: (
    pdfBase64: string,
    pickupListNo: string,
    shippingCompany: string
  ) => void;
  onStatsUpdate?: (stats: {
    totalVouchers?: number;
    printed?: number;
    unprinted?: number;
  }) => void;
}

const PickupHistory: React.FC<PickupHistoryProps> = ({
  currentUser,
  onOpenPdfPreview,
  onStatsUpdate,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [pickupLists, setPickupLists] = useState<PickupList[]>([]);
  const [loadingPdf, setLoadingPdf] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  useEffect(() => {
    loadPickupHistory();
  }, []);

  const loadPickupHistory = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/shipping/acs/pickup-lists/history?limit=100`
      );
      const result = await response.json();

      if (result.success) {
        setPickupLists(result.data.pickupLists || []);
        if (onStatsUpdate) {
          const printed = (result.data.pickupLists || []).filter(
            (pl: any) => pl.status === "printed"
          ).length;
          const issued = (result.data.pickupLists || []).filter(
            (pl: any) => pl.status === "issued"
          ).length;
          onStatsUpdate({ printed, unprinted: issued });
        }
      }
    } catch (error) {
      console.error("Error loading pickup history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (pickupList: PickupList) => {
    setLoadingPdf(pickupList.pickupListNo);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/shipping/acs/pickup-lists/print`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pickupListNo: pickupList.pickupListNo,
            pickupDate: pickupList.pickupDate,
          }),
        }
      );

      const result = await response.json();

      if (result.success && result.data.pdfBase64) {
        onOpenPdfPreview(
          result.data.pdfBase64,
          pickupList.pickupListNo,
          pickupList.shippingCompany
        );
      } else {
        alert("Failed to load PDF: " + (result.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error loading PDF:", error);
      alert("Failed to load PDF. Please try again.");
    } finally {
      setLoadingPdf(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "printed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Printed
          </span>
        );
      case "issued":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="w-3 h-3 mr-1" />
            Issued
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header Stats - Horizontal Scroll on Mobile */}
      <div className="lg:hidden overflow-x-auto -mx-4 px-4 pb-2">
        <div className="flex space-x-3 min-w-max">
          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="w-5 h-5" style={{ color: "#264a3e" }} />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Total Pickup Lists</p>
                <p className="text-xl font-semibold text-gray-900">
                  {pickupLists.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="w-5 h-5" style={{ color: "#264a3e" }} />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Printed</p>
                <p className="text-xl font-semibold text-gray-900">
                  {pickupLists.filter((pl) => pl.status === "printed").length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 flex-shrink-0" style={{ minWidth: '140px', maxWidth: '160px' }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="w-5 h-5" style={{ color: "#264a3e" }} />
              </div>
              <div className="ml-2 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 truncate">Issued</p>
                <p className="text-xl font-semibold text-gray-900">
                  {pickupLists.filter((pl) => pl.status === "issued").length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Header Stats */}
      <div className="hidden lg:grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center space-x-3">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: "#f0f4f2" }}
            >
              <FileText className="w-6 h-6" style={{ color: "#264a3e" }} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Pickup Lists</p>
              <p className="text-2xl font-bold text-gray-900">
                {pickupLists.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center space-x-3">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: "#f0f4f2" }}
            >
              <CheckCircle className="w-6 h-6" style={{ color: "#264a3e" }} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Printed</p>
              <p className="text-2xl font-bold text-gray-900">
                {pickupLists.filter((pl) => pl.status === "printed").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center space-x-3">
            <div
              className="p-3 rounded-full"
              style={{ backgroundColor: "#f0f4f2" }}
            >
              <Clock className="w-6 h-6" style={{ color: "#264a3e" }} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Issued</p>
              <p className="text-2xl font-bold text-gray-900">
                {pickupLists.filter((pl) => pl.status === "issued").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pickup Lists Table */}
      <div className="bg-white rounded-lg shadow -mx-4 sm:mx-0">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-row sm:flex-row lg:items-start sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Pickup List History
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                All issued pickup lists
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* View Toggle - Mobile Only */}
              <button
                onClick={() => setViewMode(viewMode === "table" ? "cards" : "table")}
                className="lg:hidden inline-flex items-center justify-center p-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors touch-manipulation"
                title={viewMode === "table" ? "Switch to card view" : "Switch to table view"}
              >
                {viewMode === "table" ? (
                  <LayoutGrid className="w-4 h-4" />
                ) : (
                  <List className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={loadPickupHistory}
                className="inline-flex items-center justify-center p-2 sm:px-4 sm:py-2 rounded-md bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Refresh</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : pickupLists.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No pickup lists found</p>
            </div>
          ) : viewMode === "cards" ? (
            /* Card View */
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pickupLists.map((pickupList) => (
                  <div key={pickupList._id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono font-semibold text-gray-900 mb-1 truncate">
                          {pickupList.pickupListNo}
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span>{formatDate(pickupList.pickupDate)}</span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          Issued: {formatDateTime(pickupList.issuedAt)}
                        </div>
                        <div className="mb-2">
                          {getStatusBadge(pickupList.status)}
                        </div>
                        <div className="text-xs text-gray-600 uppercase font-medium">
                          {pickupList.shippingCompany}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewPdf(pickupList)}
                      disabled={loadingPdf === pickupList.pickupListNo}
                      className="w-full flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: "#264a3e",
                        color: "white",
                      }}
                    >
                      {loadingPdf === pickupList.pickupListNo ? (
                        <>
                          <Clock className="w-4 h-4 animate-spin" />
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          <span>View PDF</span>
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Table View */
            <div className="overflow-x-auto sm:mx-0">
              <table className="w-full min-w-max">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="pl-4 pr-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Pickup List No
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Pickup Date
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider hidden lg:table-cell">
                      Issued At
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider hidden md:table-cell">
                      Company
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pickupLists.map((pickupList) => (
                    <tr key={pickupList._id} className="hover:bg-gray-50">
                      <td className="pl-4 pr-2 sm:px-4 py-3 text-xs sm:text-sm font-mono font-medium text-gray-900">
                        {pickupList.pickupListNo}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-700">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                          <span>{formatDate(pickupList.pickupDate)}</span>
                        </div>
                        {/* Show Issued At on mobile */}
                        <div className="text-xs text-gray-500 mt-1 lg:hidden">
                          {formatDateTime(pickupList.issuedAt)}
                        </div>
                        {/* Show Company on mobile */}
                        <div className="text-xs text-gray-600 uppercase font-medium mt-1 md:hidden">
                          {pickupList.shippingCompany}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-600 hidden lg:table-cell">
                        {formatDateTime(pickupList.issuedAt)}
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        {getStatusBadge(pickupList.status)}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-700 hidden md:table-cell">
                        <span className="uppercase font-medium">
                          {pickupList.shippingCompany}
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        <button
                          onClick={() => handleViewPdf(pickupList)}
                          disabled={loadingPdf === pickupList.pickupListNo}
                          className="flex items-center justify-center space-x-1 px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            backgroundColor: "#264a3e",
                            color: "white",
                          }}
                        >
                          {loadingPdf === pickupList.pickupListNo ? (
                            <>
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                              <span className="hidden sm:inline">Loading...</span>
                              <span className="sm:hidden">Loading</span>
                            </>
                          ) : (
                            <>
                              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline">View PDF</span>
                              <span className="sm:hidden">View</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickupHistory;
